import { useEffect, useMemo, useState } from 'react';

import { ListItemText, Menu, MenuItem } from '@mui/material';

import FilterButton from './FilterButton';
import { useFilterAnchor } from './useFilterAnchor';
import {
  ALGORITHM_LEVEL,
  AlgorithmLevels,
  TAlgorithmLevel,
} from '../../schemas';

import { FILTER_DEBOUNCE_TIME_MS } from '@/app/constants';
import { usePlatform } from '@/features/users';
import { Checkbox } from '@/shared/components/checkbox';

interface AlgorithmLevelFilterProps {
  filterId: string;
  buttonAriaLabel: string;
  searchParamAlgorithmLevels: AlgorithmLevels;
  handleNavigation: (selectedAlgoLevels: AlgorithmLevels) => void;
  level: TAlgorithmLevel;
}

const ALGORITHM_LEVEL_DISPLAY_NAMES: Record<TAlgorithmLevel, string> = {
  [ALGORITHM_LEVEL.ADVERTISER]: 'Advertiser',
  [ALGORITHM_LEVEL.CAMPAIGN]: 'Campaign',
  [ALGORITHM_LEVEL.INSERTION_ORDER]: 'Insertion Order',
  [ALGORITHM_LEVEL.ADGROUP]: 'Ad Group',
  [ALGORITHM_LEVEL.ADSET]: 'Ad Set',
};

const AlgorithmLevelFilter = ({
  filterId,
  buttonAriaLabel,
  searchParamAlgorithmLevels,
  handleNavigation,
  level,
}: AlgorithmLevelFilterProps) => {
  const platform = usePlatform();
  const isMeta = platform === 'Meta';
  const isDv360 = platform === 'DV360';

  const { anchorEl, handleOpen, handleClose, open, popoverId } =
    useFilterAnchor(filterId);

  const platformHierarchy: TAlgorithmLevel[] = useMemo(() => {
    if (isDv360) {
      return [ALGORITHM_LEVEL.INSERTION_ORDER, ALGORITHM_LEVEL.ADGROUP];
    }

    if (isMeta) {
      return [ALGORITHM_LEVEL.CAMPAIGN, ALGORITHM_LEVEL.ADSET];
    }

    return [
      ALGORITHM_LEVEL.ADVERTISER,
      ALGORITHM_LEVEL.CAMPAIGN,
      ALGORITHM_LEVEL.ADGROUP,
    ];
  }, [isDv360, isMeta]);

  const algorithmLevel = useMemo(() => {
    const currentIndex = platformHierarchy.indexOf(level);
    const visibleLevels = platformHierarchy.slice(0, currentIndex + 1);
    return visibleLevels.reduce<Partial<Record<TAlgorithmLevel, string>>>(
      (acc, levelKey) => {
        acc[levelKey] = ALGORITHM_LEVEL_DISPLAY_NAMES[levelKey];
        return acc;
      },
      {}
    );
  }, [platformHierarchy, level]);

  const [selectedAlgoLevels, setSelectedAlgoLevels] = useState<AlgorithmLevels>(
    searchParamAlgorithmLevels
  );
  const [isChangedInternally, setIsChangedInternally] = useState(false);

  const handleAlgoLevelChange = (lvl: TAlgorithmLevel) => {
    setIsChangedInternally(true);
    setSelectedAlgoLevels((prev) => {
      const prevAlgoLevels = prev ?? [];

      if (prevAlgoLevels.includes(lvl)) {
        const filteredLevels = prevAlgoLevels.filter((v) => v !== lvl);
        return filteredLevels.length > 0 ? filteredLevels : undefined;
      } else {
        return [...prevAlgoLevels, lvl];
      }
    });
  };

  const handleSelectAll = () => {
    setIsChangedInternally(true);

    const allLevels = Object.keys(algorithmLevel) as TAlgorithmLevel[];
    const isAllSelected = allLevels.every((id) =>
      selectedAlgoLevels?.includes(id)
    );

    if (isAllSelected) {
      setSelectedAlgoLevels(undefined);
    } else {
      setSelectedAlgoLevels(allLevels);
    }
  };

  const isAllSelected =
    (selectedAlgoLevels?.length ?? 0) > 0 &&
    Object.keys(algorithmLevel).every((key) =>
      selectedAlgoLevels?.includes(key as TAlgorithmLevel)
    );

  useEffect(() => {
    setSelectedAlgoLevels(searchParamAlgorithmLevels);
    setIsChangedInternally(false);
  }, [searchParamAlgorithmLevels]);

  useEffect(() => {
    if (!isChangedInternally) {
      return;
    }

    const timer = setTimeout(() => {
      setIsChangedInternally(false);
      handleNavigation(selectedAlgoLevels);
    }, FILTER_DEBOUNCE_TIME_MS);

    return () => {
      clearTimeout(timer);
    };
  }, [
    handleNavigation,
    isChangedInternally,
    searchParamAlgorithmLevels,
    selectedAlgoLevels,
  ]);

  return (
    <>
      <FilterButton
        isActive={!!selectedAlgoLevels?.length}
        onClick={handleOpen}
        aria-label={buttonAriaLabel}
      />
      <Menu
        id={popoverId}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      >
        <MenuItem
          key='select-all'
          onClick={handleSelectAll}
          disableGutters
          sx={{
            py: 1.25,
            pr: 1.75,
            pl: 1,
          }}
        >
          <Checkbox
            id='select-all-levels'
            checked={isAllSelected}
            indeterminate={
              !isAllSelected && (selectedAlgoLevels?.length ?? 0) > 0
            }
          />
          <ListItemText
            primary='(Select All)'
            slotProps={{
              primary: { variant: 'body3', color: 'textTertiary' },
            }}
          />
        </MenuItem>

        {Object.entries(algorithmLevel).map(([levelKey, levelTitle]) => (
          <MenuItem
            key={levelKey}
            onClick={() => {
              handleAlgoLevelChange(levelKey as TAlgorithmLevel);
            }}
            disableGutters
            sx={{
              py: 1.25,
              pr: 1.75,
              pl: 1,
            }}
          >
            <Checkbox
              id={levelKey}
              checked={
                selectedAlgoLevels?.includes(levelKey as TAlgorithmLevel) ??
                false
              }
            />
            <ListItemText
              primary={levelTitle}
              slotProps={{ primary: { variant: 'body2' } }}
            />
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default AlgorithmLevelFilter;
